import Select from "@/Components/Select/Select";
import useAllMenuDataTagsQuery from "@/shared/queries/menuDataTags/useAllMenuDataTagsQuery";
import { MenuDataTagCompact, MenuDataTagKind } from "@/shared/utils/interfaces";
import { useMemo } from "react";
import { getMenuDataTagName } from "./constants";

interface Option {
  value: number;
  label: string;
}

interface BaseProps {
  label: string;
  placeholder?: string;
  kind?: MenuDataTagKind;
}

interface SingleProps extends BaseProps {
  isMulti?: false;
  value: number | null;
  onChange: (value: number | null) => void;
}

interface MultiProps extends BaseProps {
  isMulti: true;
  value: number[];
  onChange: (value: number[]) => void;
}

const isMultiProps = (props: SingleProps | MultiProps): props is MultiProps =>
  props.isMulti === true;

const toOption = (tag: MenuDataTagCompact): Option => ({
  value: tag.item_id,
  label: `${getMenuDataTagName(tag)} (${tag.slug})`,
});

// All tags are loaded once and shared by every select, filtered on kind here
const MenuDataTagSelect = (props: SingleProps | MultiProps) => {
  const { label, placeholder, kind } = props;
  const query = useAllMenuDataTagsQuery();

  const options = useMemo(
    () =>
      (query.data ?? [])
        .filter((tag) => !kind || tag.kind?.includes(kind))
        .map(toOption),
    [query.data, kind]
  );

  // A tag that is not in the list ( anymore ) still shows its id
  const findOption = (id: number) => {
    const tag = query.data?.find((tag) => tag.item_id === id);
    return tag
      ? toOption(tag)
      : {
          value: id,
          label: `#${id}`,
        };
  };

  const value = isMultiProps(props)
    ? props.value.map(findOption)
    : props.value
      ? findOption(props.value)
      : null;

  const handleChange = (selected: Option | Option[] | null) => {
    if (isMultiProps(props)) {
      props.onChange(((selected as Option[]) ?? []).map((o) => o.value));
    } else {
      props.onChange((selected as Option | null)?.value ?? null);
    }
  };

  return (
    <Select
      isMulti={props.isMulti}
      isClearable={!props.isMulti}
      label={label}
      placeholder={placeholder}
      menuPortalTarget={document.getElementById("root")}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1000000 }) }}
      isLoading={query.isFetching}
      options={options}
      value={value}
      onChange={handleChange}
    />
  );
};

export default MenuDataTagSelect;
